// src/app/tools/random-boolean/page.tsx
'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Copy, RefreshCw, Check, Dice5 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomBooleanGeneratorContent } from '../../data/random-boolean-generator-content';
import Link from 'next/link';

export default function RandomBooleanGenerator() {
    const [result, setResult] = useState<boolean | null>(null);
    const [probability, setProbability] = useState<number>(50);
    const [showAsText, setShowAsText] = useState<boolean>(true);
    const [copied, setCopied] = useState<boolean>(false);
    const [history, setHistory] = useState<boolean[]>([]);
    const { toast } = useToast();

    const generateRandomBoolean = useCallback(() => {
        const random = Math.random() * 100;
        const newResult = random < probability;
        setResult(newResult);
        setHistory(prev => [newResult, ...prev].slice(0, 10)); // Keep last 10 results
        return newResult;
    }, [probability]);

    const handleCopy = useCallback(() => {
        if (result === null) return;

        const textToCopy = showAsText ? (result ? 'true' : 'false') : (result ? '1' : '0');
        navigator.clipboard.writeText(textToCopy);

        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Value has been copied to clipboard.',
        });

        setTimeout(() => setCopied(false), 2000);
    }, [result, showAsText, toast]);

    // Generate initial boolean on mount
    useEffect(() => {
        generateRandomBoolean();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatResult = (value: boolean): string => {
        if (showAsText) {
            return value ? 'true' : 'false';
        }
        return value ? '1' : '0';
    };

    const getResultClass = (value: boolean): string => {
        return value
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400';
    };

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="max-w-3xl mx-auto">
                <div className="mb-8 text-center">
                    <ToolPageIntro title="Random Boolean Generator">
                Generate random true/false values with our free online tool. Create boolean data for testing, simulations, and development with customizable probability.
            </ToolPageIntro>
                </div>

                <div className="grid gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Configuration</CardTitle>
                            <CardDescription>Customize your random boolean generation</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div>
                                <div className="flex justify-between mb-2">
                                    <Label>Probability of "true": {probability}%</Label>
                                    <span className="text-sm text-muted-foreground">
                                        {probability}% true, {100 - probability}% false
                                    </span>
                                </div>
                                <Slider
                                    value={[probability]}
                                    onValueChange={([value]) => setProbability(value)}
                                    min={0}
                                    max={100}
                                    step={1}
                                    className="py-4"
                                />
                            </div>

                            <div className="flex items-center justify-between pt-2">
                                <div className="space-y-1">
                                    <Label>Display as text</Label>
                                    <p className="text-sm text-muted-foreground">
                                        {showAsText ? 'true/false' : '1/0'}
                                    </p>
                                </div>
                                <Switch
                                    checked={showAsText}
                                    onCheckedChange={setShowAsText}
                                />
                            </div>

                            <div className="pt-2">
                                <Button
                                    onClick={generateRandomBoolean}
                                    className="w-full"
                                >
                                    <Dice5 className="mr-2 h-4 w-4" />
                                    Generate New Value
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <div className="flex justify-between items-center">
                                <CardTitle>Result</CardTitle>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={result === null}
                                >
                                    {copied ? (
                                        <Check className="h-4 w-4 mr-2" />
                                    ) : (
                                        <Copy className="h-4 w-4 mr-2" />
                                    )}
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div
                                className={`bg-muted/50 p-8 rounded-md text-center cursor-pointer hover:bg-muted/70 transition-colors ${result !== null ? getResultClass(result) : ''}`}
                                onClick={handleCopy}
                            >
                                {result !== null ? (
                                    <div className="text-5xl font-bold">
                                        {formatResult(result)}
                                    </div>
                                ) : (
                                    <div className="text-muted-foreground">Click generate to get started</div>
                                )}
                            </div>

                            {history.length > 0 && (
                                <div className="mt-6">
                                    <h3 className="text-sm font-medium mb-2">Recent Results</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {history.map((value, index) => (
                                            <div
                                                key={index}
                                                className={`px-3 py-1 rounded-full text-sm font-medium ${getResultClass(value)} bg-muted/50`}
                                            >
                                                {formatResult(value)}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Quick Presets</CardTitle>
                            <CardDescription>Common probability presets</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setProbability(10)}
                                >
                                    10% True
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setProbability(25)}
                                >
                                    25% True
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setProbability(50)}
                                >
                                    50/50
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setProbability(75)}
                                >
                                    75% True
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>
                        This tool generates random boolean values based on your specified probability.
                        All processing happens in your browser - no data is sent to any server.
                    </p>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomBooleanGeneratorContent.title}
                description={randomBooleanGeneratorContent.description}
                content={randomBooleanGeneratorContent.content}
                faqs={randomBooleanGeneratorContent.faqs}
                relatedTools={randomBooleanGeneratorContent.relatedTools}
            />
        </div>
    );
}